import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import jwt from 'jsonwebtoken'


export function getStorage(): Storage {
    try {
        return localStorage || sessionStorage

    } catch (error) {
        return null
    }
}

const storage: Storage = getStorage()

const sessionExpiredListeners: Array<() => void> = []

/**
 * A logout in this tab, until a request carries a token again: the navigation that follows a logout (e.g. to One's
 * logout) must not be redirected
 */
let loggedOutInThisTab = false

/** Registers a callback for when the session has ended: the API refuses it and a refresh can't fix that */
export function onSessionExpired(listener: () => void): void {
    sessionExpiredListeners.push(listener)
}

interface AuthRequestConfig extends AxiosRequestConfig {
    /** The login and refresh calls: sent without a bearer token, and their 401 means bad credentials */
    isAuthRequest?: boolean
    retried?: boolean
    /** The session (refresh token) the request was sent with */
    sentWithRefreshToken?: string | null
}

export interface DecodedAccessToken {
    iat: number
    exp: number
    roles: string[]
    email: string
    id: string
    companyId: string
}

export class AuthClient {
    protected readonly client: AxiosInstance
    protected basePath!: string

    constructor() {
        this.basePath = process.env.VUE_APP_KEYVUE_BACKEND_BASEPATH || ''
        this.client = axios.create({
            baseURL: process.env.VUE_APP_KEYVUE_BACKEND_BASEURL,
            headers: {
                'Content-Type': 'application/ld+json',
            },
        })

        this.client.interceptors.request.use((request: AxiosRequestConfig) => this.onRequest(request))
        this.client.interceptors.response.use(undefined, (error: AxiosError) => this.onError(error))
    }

    private onRequest(request: AuthRequestConfig): AxiosRequestConfig {
        // The API refuses an expired bearer token before it even looks at the login or refresh request
        if (!!this.accessToken && !request.isAuthRequest) {
            request.headers.Authorization = `Bearer ${this.accessToken}`

            // Signed in again, here or in another tab: a later expiry is reported again
            loggedOutInThisTab = false
        }

        request.sentWithRefreshToken = this.refreshToken

        return request
    }

    private async onError(error: AxiosError): Promise<any> {
        if (axios.isCancel(error)) {
            return Promise.reject(error)
        }

        if (error.response === undefined) {
            console.error(error)
            throw Error('Er kon geen verbinding gemaakt worden met de server. Probeer het later nogmaals of neem contact met ons op: support@keyvue.app')
        }

        // Refresh once and retry once. An authentication failure the refresh can't cure means the session is over: the
        // refresh token expired, or the company's license ended. Other 401s (e.g. Cumulio's, passed through) are about the request.
        const config = error.config as AuthRequestConfig
        if (this.isAuthenticationFailure(error.response) && !config.isAuthRequest) {
            const refreshToken = config.sentWithRefreshToken ?? null

            // Someone signed in since this request was sent (here or in another tab): it's not theirs to retry or to end
            if (this.isReplaced(refreshToken)) {
                return Promise.reject(error)
            }

            if (config.retried || !this.refreshToken) {
                this.endSession(refreshToken)
                return Promise.reject(error)
            }

            try {
                // A logout or sign-in during the refresh wins; after a logout elsewhere this tab leaves its page too
                if (!await this.doRefreshToken(refreshToken)) {
                    this.endSession(refreshToken)
                    return Promise.reject(error)
                }
            } catch (refreshError) {
                // Only a 401 means the refresh token is no good; without an answer from the server it may still be valid
                if (refreshError.response?.status !== 401) {
                    return Promise.reject(refreshError)
                }

                this.endSession(refreshToken)
                return Promise.reject(error)
            }

            // onRequest puts the new token on the retry
            config.retried = true
            return this.client(config)
        }

        return Promise.reject(error)
    }

    /**
     * A 401 means authentication failed (expired or missing token, license ended), except when an API Platform operation
     * answers it as its own error, like Cumulio's code passed through by /service/cumulio/authorization
     */
    private isAuthenticationFailure(response: AxiosResponse): boolean {
        return response.status === 401 && response.data?.['@type'] !== 'hydra:Error'
    }

    /** Whether storage holds another session than the given one (cleared storage is not a newer session) */
    private isReplaced(refreshToken: string | null): boolean {
        return this.refreshToken !== null && this.refreshToken !== refreshToken
    }

    /**
     * Clears the ended session's tokens and reports it; also when another tab already cleared them, so every tab leaves
     * its page. Tokens of a newer sign-in are left alone.
     */
    private endSession(refreshToken: string | null): void {
        if (this.isReplaced(refreshToken)) {
            return
        }

        this.clearTokens()

        if (loggedOutInThisTab) {
            return
        }

        // A failing listener must not replace the request's own error
        sessionExpiredListeners.forEach(listener => {
            try {
                listener()
            } catch (listenerError) {
                console.error(listenerError)
            }
        })
    }

    /** Returns whether the new tokens were stored: not when a logout or sign-in happened while the refresh was under way */
    private async doRefreshToken(refreshToken: string): Promise<boolean> {
        const response = await this.client.post('/refresh_token', {
            refresh_token: refreshToken,
        }, { isAuthRequest: true } as AuthRequestConfig)

        if (!response.data?.token || !response.data.refresh_token) {
            throw new Error('The refresh returned no tokens')
        }

        if (this.refreshToken !== refreshToken) {
            return false
        }

        this.accessToken = response.data.token
        this.refreshToken = response.data.refresh_token

        return true
    }

    async login(email: string, password: string): Promise<void> {
        const response = await this.client.post('/authentication_token', {
            email,
            password,
        }, { isAuthRequest: true } as AuthRequestConfig)

        if (storage) {
            storage.setItem('access_token', response.data.token)
            storage.setItem('refresh_token', response.data.refresh_token)
            document.cookie = `access_token=${response.data.token}`
            document.cookie = `refresh_token=${response.data.refresh_token}`
        }
    }

    async logout(): Promise<void> {
        loggedOutInThisTab = true
        this.clearTokens()
    }

    private clearTokens(): void {
        if (storage) {
            storage.removeItem('access_token')
            storage.removeItem('refresh_token')
        }
    }

    get isAuthenticated(): boolean {
        return !!this.accessToken
    }

    get accessToken(): string {
        if (storage) {
            return storage.getItem('access_token') || null
        }
    }

    set accessToken(value: string) {
        if (storage) {
            storage.setItem('access_token', value)
        }
    }

    get refreshToken(): string {
        if (storage) {
            return storage.getItem('refresh_token') || null
        }
    }

    set refreshToken(value: string) {
        if (storage) {
            storage.setItem('refresh_token', value)
        }
    }

    get decodedAccessToken(): DecodedAccessToken {
        return jwt.decode(this.accessToken) as DecodedAccessToken
    }

    get axiosClient(): AxiosInstance {
        return this.client
    }
}
